import React from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

/**
 * Reusable sortable table header cell.
 *
 * Props:
 * - label: string — column display text
 * - sortKey: string — the backend sort field name (e.g., 'po_number', 'created_at')
 * - currentSort: { key: string, order: 'asc' | 'desc' }
 * - onSort: (key: string, order: 'asc' | 'desc') => void
 * - className: string — additional styling
 * - align: 'left' | 'center' | 'right'
 *
 * Non-sortable usage: omit sortKey prop → renders plain header text
 */
const SortableHeader = ({ label, sortKey, currentSort, onSort, className = '', align = 'left' }) => {
  if (!sortKey) {
    return (
      <th className={`text-${align} px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider ${className}`}>
        {label}
      </th>
    );
  }

  const isActive = currentSort?.key === sortKey;
  const nextOrder = isActive && currentSort.order === 'desc' ? 'asc' : 'desc';

  return (
    <th
      className={`text-${align} px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider cursor-pointer select-none group transition-colors hover:bg-gray-100/50 ${
        isActive ? 'text-gray-900' : 'text-gray-500'
      } ${className}`}
      onClick={() => onSort(sortKey, nextOrder)}
      data-testid={`sort-${sortKey}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive ? (
          currentSort.order === 'desc' ? (
            <ArrowDown className="w-3 h-3 text-blue-600" />
          ) : (
            <ArrowUp className="w-3 h-3 text-blue-600" />
          )
        ) : (
          <ArrowUpDown className="w-2.5 h-2.5 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
        )}
      </span>
    </th>
  );
};

export default SortableHeader;
